import * as React from 'react';
import {
  EnterpriseRefContext,
  useEnterpriseRefContext,
} from 'providers/EnterpriseContextProvider';
import { EnterpriseModalClickSource } from 'services/BiLogger/BiLoggerTypes';

const CLICK_SOURCE_DEFAULT_VALUE = undefined;

interface EnterpriseModalContextProps {
  isModalOpen: boolean;
  clickSource: EnterpriseModalClickSource;
  closeModal: () => void;
  openModal: (clickSource?: EnterpriseModalClickSource) => void;
}

const EnterpriseModalContext = React.createContext<EnterpriseModalContextProps>(
  {
    isModalOpen: false,
    clickSource: undefined,
    openModal: () => {
      throw new Error(
        'openModal must be used within a EnterpriseModalContextProvider',
      );
    },
    closeModal: () => {
      throw new Error(
        'closeModal must be used within a EnterpriseModalContextProvider',
      );
    },
  },
);

export const useEnterpriseModalContext = () =>
  React.useContext(EnterpriseModalContext);

export const EnterpriseModalContextProvider: React.FC = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [clickSource, setClickSource] =
    React.useState<EnterpriseModalClickSource>(CLICK_SOURCE_DEFAULT_VALUE);
  const enterpriseRefContext = useEnterpriseRefContext();
  const openModal = (newClickSource: EnterpriseModalClickSource) => {
    setIsModalOpen(true);
    setClickSource(newClickSource);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setClickSource(undefined);
  };

  return (
    <EnterpriseModalContext.Provider
      value={{
        isModalOpen,
        clickSource,
        closeModal,
        openModal,
      }}
    >
      <EnterpriseRefContext.Provider
        value={{
          ...enterpriseRefContext,
          scrollToRef: openModal,
        }}
      >
        {children}
      </EnterpriseRefContext.Provider>
    </EnterpriseModalContext.Provider>
  );
};
